import { createClient } from '@/lib/supabase/client';
import { setLocale } from '@/i18n/client';
import type { Locale } from '@/i18n/request';
import { buildApiUrl } from '@/lib/api/config';

export type ThemePreference = 'light' | 'dark' | 'system';

export interface UserPreferences {
  user_id: string;
  preferred_language: Locale;
  theme_preference?: ThemePreference;
  timezone: string;
  email_notifications: boolean;
  push_notifications: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch user preferences from the backend
 */
export async function fetchUserPreferences(): Promise<UserPreferences | null> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      return null;
    }

    const response = await fetch(
      buildApiUrl('/api/preferences'),
      {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch preferences');
    }

    const data = await response.json();
    return data.preferences;
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    return null;
  }
}

/**
 * Update user preferences on the backend
 */
export async function updateUserPreferences(
  preferences: Partial<Omit<UserPreferences, 'user_id' | 'created_at' | 'updated_at'>>
): Promise<boolean> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      return false;
    }

    const response = await fetch(
      buildApiUrl('/api/preferences'),
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update preferences');
    }

    return true;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    return false;
  }
}

/**
 * Sync user's language preference from backend and apply it to the frontend
 */
export async function syncUserLanguage(): Promise<void> {
  const preferences = await fetchUserPreferences();
  
  if (preferences?.preferred_language) {
    const currentLocale = getClientLocale();
    
    // Only update if different from current
    if (currentLocale !== preferences.preferred_language) {
      setLocale(preferences.preferred_language);
    }
  }
}

/**
 * Set user language both locally and on the backend
 */
export async function setUserLanguage(language: Locale): Promise<boolean> {
  // Update backend first
  const success = await updateUserPreferences({ preferred_language: language });
  
  if (success) {
    // Then update frontend
    setLocale(language);
    return true;
  }
  
  return false;
}

/**
 * Initialize user language on first login
 * Detects browser language and saves it to backend if user has no preference
 */
export async function initializeUserLanguage(): Promise<void> {
  const preferences = await fetchUserPreferences();
  
  // If user has no language preference, set it based on browser
  if (!preferences?.preferred_language) {
    const browserLang = navigator.language.split('-')[0] as Locale;
    const supportedLangs: Locale[] = ['en', 'es', 'pt', 'fr', 'de', 'it', 'ja', 'ko', 'zh', 'ru', 'ar', 'hi', 'nl', 'pl', 'tr'];
    
    const languageToSet = supportedLangs.includes(browserLang) ? browserLang : 'en';
    
    await updateUserPreferences({ preferred_language: languageToSet });
    setLocale(languageToSet);
  } else {
    // User has preference, sync it
    await syncUserLanguage();
  }
}

/**
 * Get current locale from cookie (client-side only)
 */
function getClientLocale(): Locale {
  if (typeof window === 'undefined') return 'en';

  const LOCALE_COOKIE = 'NEXT_LOCALE';
  const stored = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${LOCALE_COOKIE}=`))
    ?.split('=')[1] as Locale | undefined;

  return stored || 'en';
}

/**
 * Get current theme from localStorage (client-side only)
 */
function getClientTheme(): ThemePreference {
  if (typeof window === 'undefined') return 'system';

  const stored = localStorage.getItem('theme') as ThemePreference | null;
  return stored || 'system';
}

/**
 * Set theme in localStorage
 */
function setClientTheme(theme: ThemePreference): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('theme', theme);
  }
}

/**
 * Sync user's theme preference from backend and apply it to the frontend
 */
export async function syncUserTheme(): Promise<void> {
  const preferences = await fetchUserPreferences();

  if (preferences?.theme_preference) {
    const currentTheme = getClientTheme();

    // Only update if different from current
    if (currentTheme !== preferences.theme_preference) {
      setClientTheme(preferences.theme_preference);
      // Dispatch event for theme provider to pick up
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('themechange', { detail: preferences.theme_preference }));
      }
    }
  }
}

/**
 * Set user theme both locally and on the backend
 */
export async function setUserTheme(theme: ThemePreference): Promise<boolean> {
  // Update local storage first for immediate feedback
  setClientTheme(theme);

  // Dispatch event for theme provider to pick up
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('themechange', { detail: theme }));
  }

  // Then update backend
  const success = await updateUserPreferences({ theme_preference: theme });

  return success;
}

/**
 * Initialize user theme on first login
 * Uses system preference if user has no preference
 */
export async function initializeUserTheme(): Promise<void> {
  const preferences = await fetchUserPreferences();

  // If user has no theme preference, set it to system
  if (!preferences?.theme_preference) {
    const currentTheme = getClientTheme();
    await updateUserPreferences({ theme_preference: currentTheme || 'system' });
  } else {
    // User has preference, sync it
    await syncUserTheme();
  }
}
